// The Hub switch: one row on every Marketing Hub page (Today, Reach out,
// Content, Growth, Results, and Settings on the right), and under it the
// pages of the place you are in. Drawing:
// docs/design/marketing-hub-redesign-mockup-v9.html, ".hubbar" / ".seg" /
// ".tabs" (approved by Grant 2026-10-29).
//
// Mounted once by PageLayout for every /marketing route, so no page has to
// remember to draw it. Pages keep their own header below it.
//
// CONSUMER: internal-ui

import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";

import type { HubPage, HubPlace } from "./hub-places";

export function MarketingHubSwitch({ places, place, page }: { places: readonly HubPlace[]; place: HubPlace; page: HubPage }) {
  const segRef = useRef<HTMLDivElement>(null);
  const work = places.filter((p) => p.key !== "settings");
  const settings = places.find((p) => p.key === "settings") ?? (place.key === "settings" ? place : undefined);

  // On a phone the switch scrolls inside itself; keep the place you are in
  // in view instead of leaving it past the edge.
  useEffect(() => {
    const seg = segRef.current;
    const on = seg?.querySelector<HTMLElement>("[aria-current]");
    if (seg && on && seg.scrollWidth > seg.clientWidth) seg.scrollLeft = Math.max(0, on.offsetLeft - seg.offsetLeft - 4);
  }, [place.key]);

  return (
    <div className="hub-switch">
      <nav className="hub-bar" aria-label="Marketing Hub">
        <div className="hub-seg" ref={segRef}>
          {work.map((p) => (
            <Link key={p.key} to={p.pages[0]?.to ?? "/marketing/today"} className={p.key === place.key ? "is-on" : undefined} aria-current={p.key === place.key ? "true" : undefined}>
              {p.label}
            </Link>
          ))}
        </div>
        {settings && (
          <Link
            to={settings.pages[0]?.to ?? "/marketing/settings"}
            className={`hub-gear${place.key === "settings" ? " is-on" : ""}`}
            aria-current={place.key === "settings" ? "true" : undefined}
            aria-label="Marketing settings"
          >
            <Settings size={18} strokeWidth={1.5} aria-hidden="true" />
            <span>Settings</span>
          </Link>
        )}
      </nav>
      {place.key !== "settings" && place.pages.length > 1 && (
        <nav className="hub-tabs" aria-label={`${place.label} pages`}>
          {place.pages.map((p) => (
            <Link key={p.to} to={p.to} className={p.to === page.to ? "is-on" : undefined} aria-current={p.to === page.to ? "page" : undefined}>
              {p.label}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
